import { useState, type FormEvent } from 'react';
import { AlertCircle, ArrowDownUp, Info, Loader2, Play, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestFtp, useTriggerFtp } from './api';
import type { FtpAnalysisDetail, FtpBucket } from './types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Phase 2.0.FTP — Fund Transfer Pricing analysis page.
 *
 * Three headline metric tiles:
 *   Total Asset FTP Cost  |  Total Liability FTP Benefit  |  Net FTP Position
 *
 * Secondary coverage tile: liabilityFtpBenefit / assetFtpCost ratio.
 *
 * Breakdown table: per-tenor-band FTP summary (7 bands from 0-1M → 5Y+).
 *
 * Degraded banner when the engine fell back to placeholder rates.
 */
export function FtpAnalysisPage() {
  const tenantId = useTenantId();
  const [date, setDate] = useState<string>(todayISO());

  const latest  = useLatestFtp(date);
  const trigger = useTriggerFtp();

  const detail: FtpAnalysisDetail | null = latest.data ?? null;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    trigger.mutate({ calculationDate: date, force: true });
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Fund Transfer Pricing</h1>
        <p className="text-sm text-muted-foreground">
          Matched-maturity FTP — transfer rates from the yield curve at each cashflow's tenor ·{' '}
          tenant{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
        </p>
      </header>

      <DegradedBanner detail={detail} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run FTP</CardTitle>
          <CardDescription>
            Each cashflow is priced at the yield-curve zero rate for its tenor. Results are
            persisted per calculation date — use force to recalculate.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 md:grid-cols-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="ftp-date">Calculation date</Label>
              <Input
                id="ftp-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="flex items-end gap-2 md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => latest.refetch()}
                disabled={latest.isFetching}
              >
                {latest.isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Reload
              </Button>
              <Button type="submit" disabled={trigger.isPending}>
                {trigger.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-2 h-4 w-4" />
                )}
                Run on-demand
              </Button>
            </div>
          </form>

          {trigger.isError ? <ApiErrorBanner error={trigger.error} /> : null}
          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation complete · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {latest.isError ? <ApiErrorBanner error={latest.error} /> : null}

      {latest.isLoading && !detail ? (
        <AnalysisSkeleton />
      ) : (
        <>
          <FtpSummaryTiles detail={detail} loading={latest.isLoading} />
          <FtpBucketTable  detail={detail} loading={latest.isLoading} />
        </>
      )}
    </div>
  );
}

// ── Degraded banner ──────────────────────────────────────────────────────────

function DegradedBanner({ detail }: { detail: FtpAnalysisDetail | null }) {
  if (!detail || !detail.degradedMode) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-800">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p>
        <strong>Degraded mode — placeholder rates used.</strong> No validated
        yield-curve snapshot was available for {detail.calculationDate}, so
        the engine fell back to the placeholder constants from{' '}
        <code>/settings/alm-parameters</code>. Upload + validate a curve on
        the <em>Parameters → Yield Curves</em> page to get curve-aware FTP rates.
      </p>
    </div>
  );
}

// ── Summary tiles ─────────────────────────────────────────────────────────

function FtpSummaryTiles({
  detail,
  loading,
}: {
  detail: FtpAnalysisDetail | null;
  loading: boolean;
}) {
  const netNegative = detail && Number(detail.netFtpPosition) < 0;
  const coverageAbove1 = detail && Number(detail.coverageRatio) > 1;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        FTP Summary [CCY]
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="Asset FTP Cost"
          value={detail ? formatCcy(detail.totalAssetFtpCost) : '—'}
          hint={loading ? 'Loading…' : detail ? 'Σ(assetNotional × ftpRate × tenorYears)' : 'No result yet'}
        />
        <Tile
          title="Liability FTP Benefit"
          value={detail ? formatCcy(detail.totalLiabilityFtpBenefit) : '—'}
          hint={detail ? 'Σ(liabilityNotional × ftpRate × tenorYears)' : '—'}
        />
        <Tile
          title="Net FTP Position"
          value={detail ? formatCcySigned(detail.netFtpPosition) : '—'}
          hint={
            detail
              ? netNegative
                ? 'Bank is net borrower at the transfer rate (typical lending bank)'
                : 'Liabilities generate more FTP income than assets cost'
              : '—'
          }
          tone={netNegative ? 'warn' : undefined}
        />
      </div>

      <p className="pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Coverage
      </p>
      <div className="grid gap-4 md:grid-cols-3">
        <Tile
          title="FTP Coverage Ratio"
          value={detail ? formatRatio(detail.coverageRatio) : '—'}
          hint={
            detail
              ? coverageAbove1
                ? 'Liabilities cover > 100% of asset funding costs'
                : 'Liabilities cover < 100% of asset funding costs'
              : '—'
          }
          tone={detail && !coverageAbove1 ? 'warn' : undefined}
        />
        <Tile
          title="Calculation date"
          value={detail ? detail.calculationDate : '—'}
          hint={detail ? `Engine ${detail.engineVersion}` : '—'}
        />
        <Tile
          title="Trigger type"
          value={detail ? detail.triggerType.replace('_', ' ') : '—'}
          hint={detail?.degradedMode ? 'Degraded mode active' : detail ? 'Curve-aware rates' : '—'}
        />
      </div>
    </div>
  );
}

// ── Bucket breakdown table ────────────────────────────────────────────────

function FtpBucketTable({
  detail,
  loading,
}: {
  detail: FtpAnalysisDetail | null;
  loading: boolean;
}) {
  if (!detail && !loading) return null;
  const buckets = detail?.bucketDetails ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <ArrowDownUp className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Per-tenor-bucket breakdown</CardTitle>
        </div>
        <CardDescription>
          FTP contribution by tenor band. Average FTP rate is notional-weighted across
          cashflows in each band.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading bucket details…
          </div>
        ) : buckets.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No bucket data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Tenor</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Avg FTP rate</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Client rate</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Spread</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Asset cost</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Liability benefit</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Net</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b: FtpBucket) => {
                  const netNeg    = Number(b.netFtp) < 0;
                  const spreadVal = Number(b.spread);
                  const spreadPos = spreadVal > 0;
                  const spreadNeg = spreadVal < 0;
                  return (
                    <tr key={b.tenorLabel} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2.5 font-medium">{b.tenorLabel}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">
                        {formatRate(b.avgFtpRate)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-blue-700">
                        {formatRate(b.clientRate)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2.5 text-right font-mono text-xs font-medium',
                          spreadPos ? 'text-emerald-700' : spreadNeg ? 'text-destructive' : 'text-muted-foreground',
                        )}
                        title="Spread = Client Rate − Avg FTP Rate"
                      >
                        {spreadPos ? '+' : ''}{formatRate(b.spread)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">
                        {formatCcy(b.assetFtpCost)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">
                        {formatCcy(b.liabilityFtpBenefit)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2.5 text-right font-mono text-xs font-medium',
                          netNeg ? 'text-destructive' : 'text-emerald-700',
                        )}
                      >
                        {formatCcySigned(b.netFtp)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/40 font-semibold">
                  <td className="px-4 py-2.5">Total</td>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {detail ? formatCcy(detail.totalAssetFtpCost) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {detail ? formatCcy(detail.totalLiabilityFtpBenefit) : '—'}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right font-mono text-xs',
                      detail && Number(detail.netFtpPosition) < 0
                        ? 'text-destructive'
                        : 'text-emerald-700',
                    )}
                  >
                    {detail ? formatCcySigned(detail.netFtpPosition) : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Shared tile ───────────────────────────────────────────────────────────

function Tile({
  title,
  value,
  hint,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  tone?: 'warn';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className={cn('text-2xl', tone === 'warn' && 'text-destructive')}>
          {value}
        </CardTitle>
      </CardHeader>
      {hint ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent>
      ) : null}
    </Card>
  );
}

// ── Formatters ────────────────────────────────────────────────────────────

/**
 * Format a large currency amount compactly (no sign prefix — always positive).
 * e.g. 12345678.1234 → "12.35M"
 */
function formatCcy(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${(abs / 1_000).toFixed(2)}K`;
  return abs.toFixed(2);
}

/**
 * Format a signed net amount (negative = cost, positive = gain).
 * e.g. -12345.67 → "−12.35K"  /  +4000 → "+4.00K"
 */
function formatCcySigned(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  const abs  = Math.abs(n);
  const sign = n < 0 ? '−' : n > 0 ? '+' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${(abs / 1_000).toFixed(2)}K`;
  return `${sign}${abs.toFixed(2)}`;
}

/** Format an FTP rate as a percentage with 4 decimal places. */
function formatRate(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  return `${(n * 100).toFixed(4)}%`;
}

/** Format coverage ratio as a decimal with 4 dp. */
function formatRatio(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return decimal;
  return n.toFixed(4);
}

// ── Error banner ──────────────────────────────────────────────────────────

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code{' '}
            <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
